import React from "react";
import { Button } from "../../atoms/Button/Button.js";
import { EmptyState, type EmptyStateSize } from "../EmptyState/EmptyState.js";

export interface SectionErrorProps {
  /** What failed, in the reader's terms. */
  title: string;
  /** The detail — the server's own message belongs here, not in the title. */
  description?: React.ReactNode;
  onRetry?: () => void;
  retryLabel?: string;
  /** `sm` renders the one-line row with a small Retry, for a small region. */
  size?: EmptyStateSize;
}

/**
 * The one rendering of a failed region, shared by `AsyncSection` and by
 * `DataTable` so a table and a form cannot report the same event differently.
 * Not exported from the package barrel: callers reach it through one of those
 * two, which is what keeps the four states together.
 */
export function SectionError({
  title,
  description,
  onRetry,
  retryLabel = "Retry",
  size = "md",
}: SectionErrorProps) {
  /* `role="alert"`, because the failure is the transition that most needs
     announcing and it is the one that was silent. The loading branch is a live
     region; when the load fails, that region UNMOUNTS and this replaces it, so
     without a role here an assistive-tech reader is left waiting on a load that
     already ended. `EmptyState` contributes no role of its own. This follows
     `shell/ModuleHome`, which wraps the identical composition in `role="alert"`
     beside a `role="status"` loading branch — the half of that precedent this
     component originally copied. */
  return (
    <div role="alert">
      <EmptyState
        variant="error"
        size={size}
        title={title}
        description={description}
        action={
          onRetry ? (
            <Button
              type="button"
              variant="secondary"
              size={size}
              onClick={onRetry}
            >
              {retryLabel}
            </Button>
          ) : undefined
        }
      />
    </div>
  );
}
